/**
 * Client-side best-line selection for slate game-line odds (ML / RL / O/U).
 *
 * The slate API returns the full per-book quote list per side; this utility picks
 * the user's "best" line, honoring `excluded_from_best_line` and `hidden_sportsbooks`
 * preferences (book ids, like quotes).
 */

import { modeOfPoints } from './modeOfPoints';
import type { BookQuote } from '@/app/api/slate/route';

export type LinePreference =
  /** Higher point favors the bettor (run line either side, totals UNDER). */
  | 'higher-point'
  /** Lower point favors the bettor (totals OVER). */
  | 'lower-point'
  /** No point exists (moneyline). */
  | 'point-irrelevant';

/** Whether a book id is among the ids the user excluded from their best-line calc. Exported so
 *  consumers that filter outside of `pickBestQuote` / `pickConsensus` (e.g. GameLinesPopover's
 *  per-market book list) apply the same rule. */
export function isExcluded(bookId: string, excludedIds: ReadonlyArray<string>): boolean {
  return excludedIds.includes(bookId);
}

/**
 * Filter out hidden books, then pick the best quote by (point favorability, price).
 * Returns undefined if no eligible quotes remain.
 */
function pickBestQuote(
  quotes: ReadonlyArray<BookQuote>,
  excludedIds: ReadonlyArray<string>,
  pref: LinePreference,
): BookQuote | undefined {
  const eligible = quotes.filter(q => !isExcluded(q.book, excludedIds));
  if (eligible.length === 0) return undefined;

  return eligible.reduce<BookQuote>((best, cur) => {
    if (pref !== 'point-irrelevant' && cur.point != null && best.point != null && cur.point !== best.point) {
      const curBetter = pref === 'higher-point' ? cur.point > best.point : cur.point < best.point;
      return curBetter ? cur : best;
    }
    return cur.price > best.price ? cur : best;
  }, eligible[0]);
}

/**
 * Pick the consensus line for a pointed market (totals / spreads).
 *
 * Each eligible book contributes one quote; we take the mode of offered points
 * (ties broken by best price) and return the best-priced quote at that point.
 * Mode, not point-favorability — a single book's alt line must not drag the
 * pill off the market consensus.
 *
 * `point` is the consensus number to display; `quote` is the best book+price to
 * show alongside it.
 */
function pickConsensus(
  quotes: ReadonlyArray<BookQuote>,
  excludedIds: ReadonlyArray<string>,
  consensusOverride?: number,
): { point?: number; quote?: BookQuote } {
  const eligible = quotes.filter(q => !isExcluded(q.book, excludedIds));
  if (eligible.length === 0) return {};

  // Prefer the server-computed consensus (book-aggregated, resists exchange
  // alt-line flooding). Fallback to naive mode if the API didn't provide one.
  const consensus = consensusOverride ?? modeOfPoints(
    eligible.map(q => q.point).filter((p): p is number => p != null),
  );

  const pool = consensus != null ? eligible.filter(q => q.point === consensus) : [];
  const pickFrom = pool.length > 0 ? pool : eligible;
  const quote = pickFrom.reduce((best, cur) => (cur.price > best.price ? cur : best));

  return { point: consensus ?? quote.point, quote };
}

export interface ResolvedGameLines {
  ml?: { home?: BookQuote; away?: BookQuote };
  rl?: { homePoint?: number; home?: BookQuote; awayPoint?: number; away?: BookQuote };
  total?: { point?: number; over?: BookQuote; under?: BookQuote };
}

const ML_PREF: LinePreference = 'point-irrelevant';

/**
 * Resolve a game's full per-book quote lists into the user's best/consensus
 * picks for moneyline, run-line/spread, and totals. Honors
 * `excluded_from_best_line` (passed as book ids). Shared between the
 * slate page and the line-movement overlay's GameContext block so both apply
 * an identical filter.
 */
export function resolveGameLines(
  odds: import('@/services/odds/slateGameOdds').GameOdds | undefined,
  excluded: ReadonlyArray<string>,
): ResolvedGameLines {
  if (!odds) return {};
  let rl: ResolvedGameLines['rl'];
  if (odds.runLine) {
    const h = pickConsensus(odds.runLine.home, excluded, odds.runLine.homeConsensus);
    const a = pickConsensus(odds.runLine.away, excluded, odds.runLine.awayConsensus);
    rl = { home: h.quote, homePoint: h.point, away: a.quote, awayPoint: a.point };
  }
  let total: ResolvedGameLines['total'];
  if (odds.total) {
    const o = pickConsensus(odds.total.over, excluded, odds.total.consensusPoint);
    const u = pickConsensus(odds.total.under, excluded, odds.total.consensusPoint);
    total = { over: o.quote, under: u.quote, point: odds.total.consensusPoint ?? o.point ?? u.point };
  }
  return {
    ml: odds.moneyline && {
      home: pickBestQuote(odds.moneyline.home, excluded, ML_PREF),
      away: pickBestQuote(odds.moneyline.away, excluded, ML_PREF),
    },
    rl,
    total,
  };
}

/**
 * Sort quotes for popover display: best first.
 * Mutates a copy — original array is not touched.
 */
export function sortQuotesForDisplay(
  quotes: ReadonlyArray<BookQuote>,
  pref: LinePreference,
): BookQuote[] {
  return [...quotes].sort((a, b) => {
    if (pref !== 'point-irrelevant' && a.point != null && b.point != null && a.point !== b.point) {
      return pref === 'higher-point' ? b.point - a.point : a.point - b.point;
    }
    return b.price - a.price;
  });
}
